/**
 * Merchant Login Page - /merchant
 * 
 * Simple landing page for merchants to enter their access code.
 * Validates the code via API, then redirects to /m/{merchant_id} with authenticated session.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, ArrowRight, Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

export default function MerchantLogin() {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!accessCode.trim()) {
      toast.error("Please enter your access code");
      return;
    }

    setLoading(true);
    
    // Clean up the access code (remove spaces, make uppercase)
    const cleanCode = accessCode.trim().toUpperCase();
    
    try {
      // Validate the access code via API and get merchant data
      const res = await apiClient.post("/b2b/merchants/login-by-code", {
        access_code: cleanCode
      });
      
      if (res.data.merchant_id) {
        // Store the authenticated session
        localStorage.setItem(`merchantToken_${res.data.merchant_id}`, res.data.token || "merchant-session");
        localStorage.setItem("merchantAccessVerified", "true");
        
        toast.success("Access verified!");
        
        // Navigate to the merchant portal with the merchant ID
        navigate(`/m/${res.data.merchant_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code. Please check and try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-viraly-dark flex flex-col items-center justify-center p-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-viraly-dark via-viraly-darker to-black opacity-90" />
      
      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="Viraly" className="h-12" />
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-viraly-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-viraly-orange" />
            </div>
            <h1 className="text-2xl font-bold text-viraly-light mb-2">
              Merchant Portal
            </h1>
            <p className="text-viraly-muted text-sm">
              Enter your access code to continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm text-viraly-muted mb-2">
                Access Code
              </label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                <Input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter your access code (e.g., M-XXXXXXXX)"
                  className="input-base pl-10 text-center uppercase tracking-wider"
                  data-testid="merchant-access-code-input"
                />
              </div>
              <p className="text-xs text-viraly-muted mt-2">
                Your access code was provided by your Enterprise administrator
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || !accessCode.trim()}
              className="btn-primary w-full"
              data-testid="merchant-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Accessing...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Help text */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-viraly-muted">
              Don't have an access code?{" "}
              <span className="text-viraly-orange">
                Contact your Enterprise administrator
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-viraly-muted text-xs mt-6">
          Powered by Viraly - AI Social Media Automation
        </p>
      </motion.div>
    </div>
  );
}
